import { Box, Chip, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crudStatusEnum } from '../../../../constants';
import { useCreateWasteCodeMutation, useUpdateWasteCodeMutation } from '../../../../hooks/mutations/useWasteCodeMutation';
import { WasteCode } from '../../../../services/apiSLTP/materials/wasteCodes';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import ToggleLine from '../../../ui/ToggleLine';
import ModalBody from '../../ModalBody';

interface ModalProps {
    wasteCode: WasteCode | null;
    isEditMode?: boolean;
    showEditModal: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: () => void;
}

const UpdateWasteCodeModal: React.FC<ModalProps> = ({ wasteCode, isEditMode = true, showEditModal, setShowEditModal, refetch }) => {
    const { t } = useTranslation();
    const [selectedDesignation, setSelectedDesignation] = useState(wasteCode?.designation ?? '');
    const [selectedDescription, setSelectedDescription] = useState(wasteCode?.description ?? '');
    const [selectedCrudStatus, setSelectedCrudStatus] = useState(wasteCode?.crudStatus ?? crudStatusEnum.OK);

    const { mutate: mutateWasteCode } = useUpdateWasteCodeMutation({
        onSuccess: () => refetch(),
        onError: (error) => console.error('Error updating waste code:', error.message),
    });

    const { mutate: addWasteCode } = useCreateWasteCodeMutation({
        onSuccess: () => refetch(),
        onError: (error) => console.error('Error creating waste code:', error.message),
    });

    const handleActiveChange = (isActive: boolean) => {
        setSelectedCrudStatus(isActive ? crudStatusEnum.OK : crudStatusEnum.INACTIVE);
    };

    const isDangerous = selectedDesignation.endsWith('*');

    return (
        <ModalBody
            title={t('wasteCode')}
            subtitle={isEditMode ? t('update') : t('add')}
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            disableSubmit={!selectedDesignation.length || !selectedDescription.length}
            onSubmit={() => {
                if (isEditMode) {
                    if (!wasteCode) return;
                    mutateWasteCode({
                        wasteCodeId: wasteCode._id,
                        data: { designation: selectedDesignation, description: selectedDescription, crudStatus: selectedCrudStatus },
                    });
                } else {
                    addWasteCode({ designation: selectedDesignation, description: selectedDescription, crudStatus: selectedCrudStatus });
                }
            }}
        >
            <CustomTextInput
                value={selectedDesignation}
                label={t('designation')}
                onChange={setSelectedDesignation}
                errorText={t('error')}
                required
                placeholder="17 01 01 ou 17 05 04*"
            />

            {isDangerous && (
                <Box sx={{ mb: 2 }}>
                    <Chip label={t('dangerousWaste')} color="error" size="small" icon={<Typography>⚠️</Typography>} />
                </Box>
            )}

            <CustomTextInput
                value={selectedDescription}
                label={t('description')}
                onChange={setSelectedDescription}
                errorText={t('error')}
                required
                multiline
                placeholder="Ex: Béton, Terres et cailloux..."
            />

            <ToggleLine checked={selectedCrudStatus === crudStatusEnum.OK} onChange={handleActiveChange} title={capitalizeFirstLetter(t('active'))} />
        </ModalBody>
    );
};

export default UpdateWasteCodeModal;
